
import type { TablesInsert } from "@/integrations/supabase/types";
import { mapGearTypeToCategory } from "@/utils/gearTypeMapping";
import { normalizeCurrencyCode } from "@/utils/currency";

interface PrepareEquipmentDataParams {
  userId?: string;
  gearName: string;
  gearType: string;
  description: string;
  address: string; // Changed from zipCode to address
  coordinates: { lat: number; lng: number } | null;
  size: string;
  skillLevel: string;
  pricePerDay: string;
  pricePerHour?: string;
  pricePerWeek?: string;
  currencyCode?: string;
  finalImageUrl: string;
  damageDeposit: string;
}

type PreparedEquipmentData = Omit<TablesInsert<"equipment">, "user_id"> & { user_id?: string };

export function prepareEquipmentData(params: PrepareEquipmentDataParams & { userId: string }): TablesInsert<"equipment">;
export function prepareEquipmentData(params: PrepareEquipmentDataParams): PreparedEquipmentData;
export function prepareEquipmentData({
  userId,
  gearName,
  gearType,
  description,
  address, // Changed from zipCode to address
  coordinates,
  size,
  skillLevel,
  pricePerDay,
  pricePerHour,
  pricePerWeek,
  currencyCode,
  finalImageUrl: _finalImageUrl,
  damageDeposit,
}: PrepareEquipmentDataParams): PreparedEquipmentData {
  // Parse numeric values
  const parsedPricePerDay = parseFloat(pricePerDay) || 0;
  const parsedPricePerHour = pricePerHour && pricePerHour.trim() ? parseFloat(pricePerHour) : null;
  const parsedPricePerWeek = pricePerWeek && pricePerWeek.trim() ? parseFloat(pricePerWeek) : null;
  const parsedDamageDeposit = damageDeposit && damageDeposit.trim() ? parseFloat(damageDeposit) : null;

  const equipmentData: PreparedEquipmentData = {
    name: gearName,
    category: mapGearTypeToCategory(gearType),
    description: description,
    location_address: address, // Changed from location_zip to location_address
    size: size,
    suitable_skill_level: skillLevel,
    price_per_day: parsedPricePerDay,
    price_per_hour: parsedPricePerHour,
    price_per_week: parsedPricePerWeek,
    currency_code: normalizeCurrencyCode(currencyCode),
    damage_deposit: parsedDamageDeposit,
    status: 'available',
    visible_on_map: true,
  };

  // Add coordinates if available
  if (coordinates) {
    equipmentData.location_lat = coordinates.lat;
    equipmentData.location_lng = coordinates.lng;
  }

  // Add user_id only if provided (for creation, not updates)
  // For updates, we never modify user_id to preserve original ownership
  if (userId) {
    equipmentData.user_id = userId;
  }

  console.log('Prepared equipment data:', equipmentData);
  return equipmentData;
}
